import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Stack,
  Avatar,
  IconButton,
  ListItemIcon,
  Button,
  Menu,
  MenuItem,
  Snackbar,
  Fade,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import EditIcon from "@mui/icons-material/Edit";
import LanguageIcon from "@mui/icons-material/Language";
import SchoolIcon from "@mui/icons-material/School";
import GavelIcon from "@mui/icons-material/Gavel";
import HistoryIcon from "@mui/icons-material/History";
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import i18n from "../../i18n";
import { GetUserInfo, ListUpdate } from "../../services/user/user";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { convertToJalali, convertToJalaliNoTime } from "../../utils/convertToJalali";

const languageOptions = [
  { value: "en", label: "English" },
  { value: "fa", label: "فارسی" },
];



const SettingsPage = () => {
  const { t } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const lang = i18n.language || "fa"; // default to fa if undefined
  const [version, setVersion] = useState("");

  const [language, setLanguage] = React.useState(i18n.language);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [userInfo, setUserInfo] = React.useState({
    phone: "",
    datesabt: "",
    type: 0,
  });
  const navigate = useNavigate();
  const items = [
    {
      text: "settings.studentPanel",
      icon: <SchoolIcon color="primary" />,
      href: `https://edu.copysa.ir/${lang}`, // dynamic based on language
    },
    {
      text: "settings.termsOfAgreement",
      icon: <GavelIcon color="primary" />,
      href: "/agreement",
    },
    {
      text: "Sidebar.History",
      icon: <HistoryIcon color="primary" />,
      href: "/history",
    },
  ];
  React.useEffect(() => {
    const onLanguageChanged = (lng) => setLanguage(lng);
    i18n.on("languageChanged", onLanguageChanged);
    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, []);
  const handleClick = (href) => {
    if (href.startsWith("http")) {
      window.open(href, "_blank", "noopener,noreferrer"); // opens external link in new tab
    } else {
      navigate(href); // internal navigation
    }
  };
  const getVersion = async () => {
    try {
      const res = await ListUpdate();
      const ver = res.data.version;
      setVersion(ver);
    } catch (error) {}
  };
  useEffect(() => {
    getVersion();
  }, []);
  React.useEffect(() => {
    // Fetch user info on mount
    GetUserInfo()
      .then((res) => {
        setUserInfo(res.data);
      })
      .catch(() => {
        setUserInfo({ phone: "-", datesabt: "-", type: 0 });
      });
  }, []);

  const handleLangClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLangSelect = (lang) => {
    i18n.changeLanguage(lang);
    document.cookie = `i18next=${lang};path=/;domain=.copysa.ir;max-age=31536000`; // 1 year
    setAnchorEl(null);
  };
  function formatJoinDate(dateString) {
    if (!dateString) return "-";

    if (i18n.language === "fa") {
      return convertToJalaliNoTime (dateString);
    }

    const date = new Date(dateString);
    return date.toLocaleDateString();
  }
  // Determine account type label
  let accountTypeLabel = "Normal";
  if (userInfo.type === 2) accountTypeLabel = "Student";
  else if (userInfo.type === 0) accountTypeLabel = "Normal";

  return (
    <Box
      sx={{
        py: { xs: 3, sm: 4 },
        bgcolor: "background.default",
        maxWidth: 600,
        mx: "auto",
      }}
    >
      {/* User Info */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              width: { xs: 48, sm: 56 },
              height: { xs: 48, sm: 56 },
              bgcolor: "primary.main",
              transition: "transform 0.2s",
              "&:hover": { transform: "scale(1.05)" },
            }}
            aria-label={t("aria.userAvatar")}
          >
            <PersonIcon fontSize="medium" />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {userInfo.phone || t("settings.unknown")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("settings.joined", {
                date: formatJoinDate(userInfo.datesabt),
              })}
            </Typography>
            <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
              {t("settings.accountType", {
                type: t(
                  userInfo.type === 2 ? "settings.student" : "settings.normal"
                ),
              })}
            </Typography>
          </Box>
          <Box flexGrow={1} />
          <IconButton
            size="small"
            color="primary"
            aria-label={t("aria.editProfile")}
            onClick={() => navigate("/profile")}
            sx={{
              "&:hover": {
                bgcolor: "primary.light",
                color: "primary.contrastText",
              },
            }}
          >
            <EditIcon />
          </IconButton>
        </Stack>
      </Box>

      <Divider />

      {/* Language Section */}
      <Typography
        variant="subtitle2"
        sx={{ px: 2, pt: 1, pb: 1, color: "text.secondary" }}
      >
        {t("settings.language")}
      </Typography>
      <List disablePadding>
        <ListItemButton
          onClick={handleLangClick}
          sx={{
            py: 1.5,
            transition: "background-color 0.2s",
            "&:hover": { bgcolor: "action.hover" },
          }}
          aria-label={t("aria.selectAppLanguage")}
        >
          <ListItemIcon>
            <LanguageIcon color="primary" />
          </ListItemIcon>
          <ListItemText
            primary={t("settings.appLanguage")}
            secondary={` ${
              languageOptions.find((l) => l.value === language)?.label
            }`}
            primaryTypographyProps={{ variant: "body1" }}
            secondaryTypographyProps={{ variant: "body2" }}
          />
          {isRtl ? (
            <ArrowBackIosNewIcon fontSize="small" color="disabled" />
          ) : (
            <ArrowForwardIosIcon fontSize="small" color="disabled" />
          )}
        </ListItemButton>
      </List>

      {/* Language Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        TransitionComponent={Fade}
        PaperProps={{
          sx: { minWidth: 200, mt: 1 },
        }}
      >
        {languageOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleLangSelect(option.value)}
            selected={language === option.value}
            sx={{ py: 1.5 }}
            aria-label={t("aria.selectLanguage", { lang: option.label })}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography>{option.icon}</Typography>
              <Typography>{option.label}</Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>

      <Divider sx={{ my: 1 }} />

      {/* Account Section */}
      <Typography
        variant="subtitle2"
        sx={{ px: 2, pb: 1, color: "text.secondary" }}
      >
        {t("settings.account")}
      </Typography>
      <List disablePadding mb={2}>
        {items.map((item, index) => (
          <React.Fragment key={item.text}>
            <ListItemButton
              onClick={() => handleClick(item.href)}
              sx={{
                py: 1.5,
                transition: "background-color 0.2s",
                "&:hover": { bgcolor: "action.hover" },
              }}
              aria-label={t(item.text)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText
                primary={t(item.text)}
                primaryTypographyProps={{ variant: "body1" }}
              />
              {isRtl ? (
                <ArrowBackIosNewIcon fontSize="small" color="disabled" />
              ) : (
                <ArrowForwardIosIcon fontSize="small" color="disabled" />
              )}
            </ListItemButton>
            {index < items.length - 1 && <Divider component="li" />}
          </React.Fragment>
        ))}
      </List>

      {/* Footer */}
      <Box sx={{ px: 2, pt: 1 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={{ xs: 2, sm: 0 }}
        >
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            sx={{ borderRadius: 2, textTransform: "none", py: 1 }}
            aria-label={t("aria.logout")}
          >
            {t("settings.logOut")}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t("settings.version", { version: version })}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default SettingsPage;
